import { useCallback, useEffect, useRef, useState } from "react";
import { api, API, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ShieldCheck, Lock, Upload, Download, Trash2, FileText, Plus } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["legal", "hr", "contract", "litigation", "confidential", "board_only", "other"];

const CAT_TONE = {
  legal: "border-indigo-500/40 text-indigo-300 bg-indigo-500/10",
  hr: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
  contract: "border-amber-500/40 text-amber-300 bg-amber-500/10",
  litigation: "border-rose-500/40 text-rose-300 bg-rose-500/10",
  confidential: "border-orange-500/40 text-orange-300 bg-orange-500/10",
  board_only: "border-violet-500/40 text-violet-300 bg-violet-500/10",
  other: "border-white/10 text-zinc-400",
};

export default function BoardVaultPage() {
  const { propertyId, properties } = useProperty();
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    property_id: propertyId || "",
    display_name: "",
    description: "",
    category: "legal",
  });
  const fileRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = propertyId ? { property_id: propertyId } : {};
      const { data } = await api.get("/board/vault", { params });
      setItems(data.items || []);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load vault"); }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setForm((f) => ({ ...f, property_id: propertyId || f.property_id })); }, [propertyId]);

  const upload = async () => {
    if (!selectedFile) return toast.error("Choose a file first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", selectedFile);
      fd.append("property_id", form.property_id);
      fd.append("category", form.category);
      if (form.display_name) fd.append("display_name", form.display_name);
      if (form.description) fd.append("description", form.description);
      await api.post("/board/vault/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded to encrypted vault");
      setSelectedFile(null);
      setForm({ property_id: propertyId, display_name: "", description: "", category: "legal" });
      setDialogOpen(false);
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Upload failed"); }
    setBusy(false);
  };

  const remove = async (d) => {
    if (!window.confirm(`Archive "${d.display_name}"? Hard delete is uploader-only.`)) return;
    try {
      await api.delete(`/board/vault/${d.id}`);
      toast.success("Archived");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Delete failed"); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Governance"
        title="Board Vault"
        subtitle="Encrypted-at-rest storage for HR letters, legal drafts, and other director-only material. Files are AES-encrypted before hitting object storage — even ops can't peek."
      />

      <div className="flex items-center gap-3 mb-4">
        <Button onClick={() => setDialogOpen(true)} className="bg-orange-500 hover:bg-orange-600 rounded-full"
                data-testid="vault-add-btn">
          <Plus className="h-4 w-4 mr-1" /> Upload to vault
        </Button>
        <div className="flex items-center gap-1 text-xs text-zinc-500">
          <ShieldCheck className="h-3 w-3 text-emerald-400" /> AES-256 · Fernet · Encrypted before upload
        </div>
        <div className="ml-auto text-xs text-zinc-500">{items.length} doc{items.length === 1 ? "" : "s"}</div>
      </div>

      {items.length === 0 ? (
        <Card className="p-8 text-center border-white/10" data-testid="vault-empty">
          <Lock className="h-8 w-8 mx-auto text-zinc-500 mb-2" />
          <div className="text-sm text-zinc-400">
            The vault is empty. Upload sensitive HR letters, litigation drafts, or confidential contracts — everything you don&apos;t want in the general documents library.
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((d) => (
            <Card key={d.id} className="p-4 border-white/10 bg-card" data-testid={`vault-row-${d.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="h-4 w-4 text-orange-400" />
                    <div className="text-sm font-semibold truncate">{d.display_name}</div>
                    <Badge variant="outline" className={CAT_TONE[d.category] || CAT_TONE.other}>
                      {d.category}
                    </Badge>
                  </div>
                  {d.description && <div className="text-xs text-zinc-400">{d.description}</div>}
                  <div className="text-[11px] text-zinc-500 mt-1">
                    Uploaded {new Date(d.uploaded_at).toLocaleString()} · {d.uploaded_by_name} ({d.uploaded_by_role})
                  </div>
                  <div className="text-[10px] text-zinc-500 font-mono mt-1">
                    {(d.size_plain / 1024).toFixed(1)} KB plain · {(d.size_encrypted / 1024).toFixed(1)} KB encrypted
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <a href={`${API}/board/vault/${d.id}/download`}
                     target="_blank" rel="noreferrer"
                     data-testid={`vault-download-${d.id}`}
                     className="inline-flex items-center px-2 py-1 text-xs rounded-full border border-white/10 text-zinc-300 hover:bg-white/5">
                    <Download className="h-3 w-3 mr-1" /> Download
                  </a>
                  <button onClick={() => remove(d)}
                          className="text-zinc-500 hover:text-red-300 p-1"
                          data-testid={`vault-delete-${d.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-[520px]" data-testid="vault-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-orange-400" /> Upload to encrypted vault
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              Files are AES-encrypted client-relative before object storage sees them. Only board members and admins on this community can read the plaintext.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div>
              <Label className="text-xs text-zinc-400">Community</Label>
              <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}>
                <SelectTrigger className="bg-white/[0.04] border-white/10" data-testid="vault-property">
                  <SelectValue placeholder="Choose a community" />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                  {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-zinc-400">Display name</Label>
                <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                       placeholder="Q1 legal memo" className="bg-white/[0.04] border-white/10" data-testid="vault-name" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="bg-white/[0.04] border-white/10" data-testid="vault-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Description</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                        placeholder="Context for future board members…" className="bg-white/[0.04] border-white/10 text-sm"
                        data-testid="vault-description" />
            </div>
            <input ref={fileRef} type="file" onChange={(e) => setSelectedFile(e.target.files?.[0])}
                   accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.png,.jpg,.jpeg,.webp"
                   className="text-xs text-zinc-300" data-testid="vault-file-input" />
            {selectedFile && <div className="text-[11px] text-orange-300">{selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={busy} className="border-white/10">Cancel</Button>
            <Button onClick={upload} disabled={busy || !selectedFile || !form.property_id}
                    className="bg-orange-500 hover:bg-orange-600" data-testid="vault-save-btn">
              <Upload className="h-4 w-4 mr-1" /> {busy ? "Encrypting & uploading…" : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
